import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Grid,
  Card,
  CardContent,
  Stack,
  Tooltip,
  Button,
  Avatar,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Divider,
  Popover
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import FilterListIcon from '@mui/icons-material/FilterList'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import CommentIcon from '@mui/icons-material/Comment'
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied'
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral'
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied'
import DownloadIcon from '@mui/icons-material/Download'
import RefreshIcon from '@mui/icons-material/Refresh'
import PsychologyIcon from '@mui/icons-material/Psychology'
import InsightsIcon from '@mui/icons-material/Insights'
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions'
import ClearIcon from '@mui/icons-material/Clear'
import ReactECharts from 'echarts-for-react'
import { EChartsOption } from 'echarts'
import { TikTokAnalytics } from '../components/visualizations/TikTokAnalytics'
import allCommentsDataImport from '../data/tiktokAllComments.json'

// Convert array-like object to proper array - handle safely
const allCommentsData = {
  metadata: (allCommentsDataImport as any).metadata || { totalComments: 0, videos: {} },
  comments: (allCommentsDataImport as any).comments ||
    Array.from({ length: (allCommentsDataImport as any).length || 0 }, (_, i) => (allCommentsDataImport as any)[i]).filter(Boolean),
  summary: (allCommentsDataImport as any).summary || { sentiment: { positive: 0, negative: 0, neutral: 0 } }
}

interface Comment {
  text: string
  author: string
  likes: number
  replies: number
  created?: string
  category?: string
  categoryName?: string
  confidence?: number
  sentiment?: string
  positiveRelevant?: boolean
  positiveAttributed?: boolean
  videoType?: string
  themes?: Array<{ name: string; confidence: number }>
  entities?: {
    people?: string[]
    brands?: string[]
    media?: string[]
  }
  aiAnalysis?: any
}

interface TikTokData {
  video_url: string
  total_comments: number
  comments: Comment[]
  summary?: any
  metadata?: any
}

type Order = 'asc' | 'desc'
type CategoryFilter = 'all' | string
type SentimentFilter = 'all' | 'positive' | 'negative' | 'neutral' | 'relevant' | 'attributed'

const categoryColors: Record<string, string> = {
  // Content type values (from actual data)
  'substantive': '#2196f3',
  'brief': '#4caf50',
  'meme': '#ff9800',
  'emoji': '#ffc107',
  'spam': '#f44336',
  'unknown': '#757575',
  // Keys with underscores (from data)
  'substantive_feedback': '#2196f3',
  'reference_culture': '#9c27b0',
  'brief_praise': '#4caf50',
  'brief_criticism': '#f44336',
  'nonsense_meme': '#ff9800',
  'emoji_only': '#ffc107',
  'commercial_awareness': '#00bcd4',
  'request_continuation': '#673ab7',
  'inside_joke': '#e91e63',
  'engagement_bait': '#795548',
  // Readable names (displayed)
  'Substantive Feedback': '#2196f3',
  'Cultural/Media Reference': '#9c27b0',
  'Brief Praise/Support': '#4caf50',
  'Brief Criticism': '#f44336',
  'Nonsense/Meme Text': '#ff9800',
  'Emoji Only': '#ffc107',
  'Commercial/Partnership Awareness': '#00bcd4',
  'Request for Continuation': '#673ab7',
  'Inside Joke/Community Reference': '#e91e63',
  'Engagement Bait': '#795548'
}

const TiktokAnalysis: React.FC = () => {
  const [data, setData] = useState<TikTokData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Table state
  const [order, setOrder] = useState<Order>('desc')
  const [orderBy, setOrderBy] = useState<keyof Comment>('likes')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [searchQuery, setSearchQuery] = useState('')

  // Filters
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all')
  const [sentimentFilter, setSentimentFilter] = useState<SentimentFilter>('all')
  const [selectedVideo, setSelectedVideo] = useState<string>('sports')

  // Emoji picker state
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLButtonElement | null>(null)

  // Video thumbnails - using local images
  const videoThumbnails = {
    sports: '/img/osKMZAAwQBLafNkhFSLIAGGZEYArbkAQIeeZIT~tplv-tiktokx-origin.png',
    brand: '/img/oo9C2iwBwDOAkujiPR0ICBA4IlUZ8SI4nf5OLy~tplv-tiktokx-origin.jfif',
    influencer: '/img/osERXeQ0nACdA0DuCgfoFAAkAMIliA0WFkGohE.jfif'
  }

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    setError(null)

    try {
      // Try to load from API first
      const apiResponse = await fetch('/api/social/tiktok-ai-analysis')
      if (apiResponse.ok) {
        const apiData = await apiResponse.json()
        setData({
          video_url: apiData.metadata?.video_url || 'https://www.tiktok.com/@camfitzzz/video/7501381885688368406',
          total_comments: apiData.metadata?.totalComments || 100,
          comments: apiData.comments || [],
          summary: apiData.summary,
          metadata: apiData.metadata
        })
      } else {
        // Use combined data as fallback
        setData({
          video_url: 'Multiple TikTok Videos',
          total_comments: allCommentsData.metadata.totalComments,
          comments: allCommentsData.comments as any as Comment[],
          summary: allCommentsData.summary,
          metadata: allCommentsData.metadata
        })
      }
    } catch (err) {
      // Use combined data as fallback on any error
      console.log('Using combined TikTok data with all comments')
      setData({
        video_url: 'Multiple TikTok Videos',
        total_comments: allCommentsData.metadata.totalComments,
        comments: allCommentsData.comments as any as Comment[],
        summary: allCommentsData.summary,
        metadata: allCommentsData.metadata
      })
    } finally {
      setLoading(false)
    }
  }

  const handleRequestSort = (property: keyof Comment) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }


  const getSentimentIcon = (sentiment?: string) => {
    switch (sentiment) {
      case 'positive':
        return <SentimentSatisfiedIcon sx={{ color: '#4caf50' }} />
      case 'negative':
        return <SentimentDissatisfiedIcon sx={{ color: '#f44336' }} />
      default:
        return <SentimentNeutralIcon sx={{ color: '#9e9e9e' }} />
    }
  }

  // Get available categories for the current video selection
  const availableCategories = useMemo(() => {
    if (!data) return new Set<string>()

    let commentsToCheck = [...data.comments]

    // Apply video filter to determine which categories are available
    if (selectedVideo !== 'all') {
      commentsToCheck = commentsToCheck.filter(comment => {
        const caseField = (comment as any).Case
        if (!caseField) return false
        if (selectedVideo === 'sports') return caseField.startsWith('Sports_')
        if (selectedVideo === 'brand') return caseField.startsWith('Brand_')
        if (selectedVideo === 'influencer') return caseField.startsWith('Influencer_')
        return false
      })
    }

    // Get unique categories from filtered comments (using contentType field)
    const categories = new Set<string>()
    commentsToCheck.forEach(comment => {
      if ((comment as any).contentType) {
        categories.add((comment as any).contentType)
      }
    })

    return categories
  }, [data, selectedVideo])

  const filteredAndSortedComments = useMemo(() => {
    if (!data) return []

    let filtered = [...data.comments]

    // Apply video filter
    if (selectedVideo !== 'all') {
      filtered = filtered.filter(comment => {
        const caseField = (comment as any).Case
        if (!caseField) return false
        if (selectedVideo === 'sports') return caseField.startsWith('Sports_')
        if (selectedVideo === 'brand') return caseField.startsWith('Brand_')
        if (selectedVideo === 'influencer') return caseField.startsWith('Influencer_')
        return false
      })
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(comment => {
        // Check text and author
        if (comment.text.toLowerCase().includes(query) ||
            comment.author.toLowerCase().includes(query)) {
          return true
        }
        // Check themes (which is an object)
        if ((comment as any).themes) {
          const themeNames = Object.keys((comment as any).themes)
          return themeNames.some(theme => theme.toLowerCase().includes(query))
        }
        return false
      })
    }

    // Apply category filter (using contentType)
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(comment => (comment as any).contentType === categoryFilter)
    }

    // Apply sentiment filter
    if (sentimentFilter !== 'all') {
      if (sentimentFilter === 'relevant') {
        // Filter for positive comments that are relevant to Sky
        filtered = filtered.filter(comment =>
          comment.sentiment === 'positive' && (comment as any).positiveRelevant === true
        )
      } else if (sentimentFilter === 'attributed') {
        // Filter for positive comments that are attributed to Sky
        filtered = filtered.filter(comment =>
          comment.sentiment === 'positive' && (comment as any).positiveAttributed === true
        )
      } else {
        // Standard sentiment filters (positive, neutral, negative)
        filtered = filtered.filter(comment => comment.sentiment === sentimentFilter)
      }
    }

    // Sort
    filtered.sort((a, b) => {
      const aValue = a[orderBy] || 0
      const bValue = b[orderBy] || 0

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return order === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue
      }

      return 0
    })

    return filtered
  }, [data, selectedVideo, searchQuery, categoryFilter, sentimentFilter, order, orderBy])

  const summaryData = useMemo(() => {
    if (!data) return null

    // Filter data based on selected video
    const filteredComments = selectedVideo === 'all'
      ? data.comments
      : data.comments.filter(c => {
          // Match based on Case field (e.g., Sports_001, Brand_001, Influencer_001)
          const caseField = (c as any).Case
          if (!caseField) return false

          if (selectedVideo === 'sports') return caseField.startsWith('Sports_')
          if (selectedVideo === 'brand') return caseField.startsWith('Brand_')
          if (selectedVideo === 'influencer') return caseField.startsWith('Influencer_')
          return false
        })

    // Calculate stats from filtered comments
    const categoryCount: Record<string, number> = {}
    const sentimentCount = { positive: 0, negative: 0, neutral: 0 }
    const positiveBreakdown = { relevant: 0, attributed: 0, relevantAndAttributed: 0 }
    const themeCount: Record<string, number> = {}
    const categoryEmotionMap: Record<string, Record<string, number>> = {}

    // Extract emotion data from metadata if available
    if (selectedVideo === 'all' && data.metadata?.videos) {
      // Aggregate emotions by category from all videos
      Object.values(data.metadata.videos as any).forEach((videoData: any) => {
        if (videoData?.summary?.emotions) {
          // We'll map emotions to categories based on the comments
        }
      })
    }

    filteredComments.forEach(comment => {
      // Count categories - use contentType as category
      const catName = (comment as any).contentType || 'unknown'
      if (catName) {
        categoryCount[catName] = (categoryCount[catName] || 0) + 1

        // Initialize emotion map for this category if needed
        if (!categoryEmotionMap[catName]) {
          categoryEmotionMap[catName] = {}
        }

        // Map comment to emotions (we'll use primary emotion from aiAnalysis if available)
        const primaryEmotion = (comment as any).primaryEmotion
        if (primaryEmotion && primaryEmotion !== 'none') {
          categoryEmotionMap[catName][primaryEmotion] = (categoryEmotionMap[catName][primaryEmotion] || 0) + 1
        }
      }
      // Count sentiments
      if (comment.sentiment) {
        sentimentCount[comment.sentiment as keyof typeof sentimentCount]++

        // Count positive breakdown if sentiment is positive
        if (comment.sentiment === 'positive') {
          const isRelevant = (comment as any).positiveRelevant || false
          const isAttributed = (comment as any).positiveAttributed || false

          if (isRelevant) positiveBreakdown.relevant++
          if (isAttributed) positiveBreakdown.attributed++
          if (isRelevant && isAttributed) positiveBreakdown.relevantAndAttributed++
        }
      }
      // Count themes - themes is an object with relevance levels
      if ((comment as any).themes) {
        Object.entries((comment as any).themes).forEach(([themeName, relevance]) => {
          if (relevance && relevance !== 'none' && relevance !== 'low') {
            // Only count medium and high relevance themes
            // Convert theme keys to readable names
            const themeNameMap: Record<string, string> = {
              productQuality: 'Product/Service Quality',
              brandPerception: 'Brand Perception',
              entertainmentValue: 'Entertainment Value',
              authenticity: 'Authenticity/Sponsorship',
              contentCreator: 'Content Creator',
              callToAction: 'Call to Action Response',
              comparison: 'Comparison/Competition',
              personalExperience: 'Personal Experience',
              memesInternet: 'Memes/Internet Culture',
              technicalInfo: 'Technical/Informational'
            }
            const displayName = themeNameMap[themeName] || themeName
            themeCount[displayName] = (themeCount[displayName] || 0) + 1
          }
        })
      }
    })

    const totalFiltered = filteredComments.length

    return {
      video_url: selectedVideo === 'all' ? 'All Videos' : `${selectedVideo.charAt(0).toUpperCase() + selectedVideo.slice(1)} Video`,
      total_comments: totalFiltered,
      categories: Object.entries(categoryCount).map(([name, count]) => ({
        category: name,
        count,
        percentage: ((count / totalFiltered) * 100).toFixed(1) + '%'
      })),
      sentiment: {
        positive_percentage: ((sentimentCount.positive / totalFiltered) * 100).toFixed(1),
        negative_percentage: ((sentimentCount.negative / totalFiltered) * 100).toFixed(1),
        neutral_percentage: ((sentimentCount.neutral / totalFiltered) * 100).toFixed(1)
      },
      positiveBreakdown: sentimentCount.positive > 0 ? positiveBreakdown : undefined,
      top_themes: Object.entries(themeCount).sort(([, a], [, b]) => b - a).slice(0, 5).map(([theme, count]) => ({
        theme,
        count
      })),
      insights: {
        substantive_content_ratio: categoryCount['substantive'] ? ((categoryCount['substantive'] / totalFiltered) * 100).toFixed(1) + '%' : '0%',
        meme_nonsense_ratio: categoryCount['meme'] ? ((categoryCount['meme'] / totalFiltered) * 100).toFixed(1) + '%' : '0%',
        commercial_awareness: '0%' // No commercial awareness data in current structure
      },
      comments: filteredComments,
      emotionData: categoryEmotionMap
    }
  }, [data, selectedVideo])

  // Category Distribution chart (replacing emotion chart)
  const categoryChartOption: EChartsOption = useMemo(() => {
    if (!data) return {} as EChartsOption

    // Define all possible content types (categories)
    const allCategories = [
      'substantive',
      'brief',
      'meme',
      'emoji',
      'spam',
      'unknown'
    ]

    // Get actual counts from summaryData
    const categoryMap = new Map<string, number>()
    summaryData?.categories?.forEach(c => {
      categoryMap.set(c.category, c.count)
    })

    // Create data for all categories, using 0 for missing ones
    const categoryData = allCategories.map(category => ({
      category,
      count: categoryMap.get(category) || 0
    }))

    return {
      title: {
        text: 'Category Distribution',
        left: 'center'
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: categoryData.map(c => c.category),
        axisLabel: {
          interval: 0,
          rotate: 45,
          fontSize: 9
        }
      },
      yAxis: {
        type: 'value',
        name: 'Comments'
      },
      series: [{
        data: categoryData.map(c => ({
          value: c.count,
          itemStyle: {
            color: categoryColors[c.category] || '#999'
          }
        })),
        type: 'bar',
        label: {
          show: true,
          position: 'top',
          formatter: (params: any) => params.value > 0 ? params.value : ''
        }
      }]
    } as EChartsOption
  }, [data, summaryData])

  // Sentiment Pie Chart
  const sentimentChartOption: EChartsOption = useMemo(() => {
    if (!summaryData) return {} as EChartsOption

    return {
      title: {
        text: 'Sentiment Analysis',
        left: 'center'
      },
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)'
      },
      series: [{
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: '#fff',
          borderWidth: 2
        },
        label: {
          show: true,
          formatter: '{b}\n{d}%'
        },
        data: [
          {
            value: parseFloat(summaryData.sentiment.positive_percentage),
            name: 'Positive',
            itemStyle: { color: '#4caf50' }
          },
          {
            value: parseFloat(summaryData.sentiment.neutral_percentage),
            name: 'Neutral',
            itemStyle: { color: '#ff9800' }
          },
          {
            value: parseFloat(summaryData.sentiment.negative_percentage),
            name: 'Negative',
            itemStyle: { color: '#f44336' }
          }
        ]
      }]
    } as EChartsOption
  }, [summaryData])

  // Emotion Bar Chart
  const emotionChartOption: EChartsOption = useMemo(() => {
    if (!data) return {} as EChartsOption

    const emotionCount: Record<string, number> = {}

    // If showing all videos, aggregate emotions from all comments
    if (selectedVideo === 'all') {
      // Aggregate emotions from all video summaries
      if (data.metadata?.videos) {
        Object.values(data.metadata.videos as any).forEach((videoData: any) => {
          if (videoData?.summary?.emotions) {
            Object.entries(videoData.summary.emotions).forEach(([emotion, count]) => {
              emotionCount[emotion] = (emotionCount[emotion] || 0) + (count as number)
            })
          }
        })
      }

    } else {
      // Single video emotion data
      if (!data.metadata?.videos) return {} as EChartsOption
      const videoData = (data.metadata.videos as any)[selectedVideo]
      if (videoData?.summary?.emotions) {
        Object.entries(videoData.summary.emotions).forEach(([emotion, count]) => {
          emotionCount[emotion] = count as number
        })
      }
    }

    const emotionColors: Record<string, string> = {
      'Joy': '#4caf50',
      'Trust': '#2196f3',
      'Surprise': '#ff9800',
      'Anticipation': '#9c27b0',
      'Anger': '#f44336',
      'Sadness': '#607d8b',
      'Disgust': '#795548',
      'Fear': '#e91e63'
    }

    const emotionData = Object.entries(emotionCount)
      .filter(([_, count]) => count > 0)
      .map(([name, count]) => ({
        name,
        value: count
      }))
      .sort((a, b) => b.value - a.value)

    return {
      title: {
        text: 'Emotion Distribution',
        left: 'center',
        textStyle: {
          fontSize: 14,
          fontWeight: 600,
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      xAxis: {
        type: 'category',
        data: emotionData.map(e => e.name),
        axisLabel: {
          rotate: 45,
          fontSize: 10
        }
      },
      yAxis: {
        type: 'value',
        name: 'Count'
      },
      series: [{
        type: 'bar',
        data: emotionData.map(item => ({
          value: item.value,
          itemStyle: { color: emotionColors[item.name] || '#999' }
        }))
      }]
    } as EChartsOption
  }, [data, selectedVideo])

  // Video insights
  const videoInsights = useMemo(() => {
    if (!data) return null

    // For 'all' videos, aggregate insights
    if (selectedVideo === 'all') {
      const allComments = data.comments
      const topComments = allComments
        .sort((a: any, b: any) => (b.likes || 0) - (a.likes || 0))
        .slice(0, 3)

      // Aggregate sentiment
      const sentiment = { positive: 0, negative: 0, neutral: 0 }
      allComments.forEach((c: any) => {
        if (c.sentiment) sentiment[c.sentiment as keyof typeof sentiment]++
      })

      // Aggregate emotions
      const emotions: Record<string, number> = {}
      if (data.metadata?.videos) {
        Object.values(data.metadata.videos as any).forEach((videoData: any) => {
          if (videoData?.summary?.emotions) {
            Object.entries(videoData.summary.emotions).forEach(([emotion, count]) => {
              emotions[emotion] = (emotions[emotion] || 0) + (count as number)
            })
          }
        })
      }

      return {
        totalComments: data.total_comments,
        sentiment,
        topEmotions: Object.entries(emotions)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 3)
          .map(([emotion, count]) => ({ emotion, count })),
        topComments,
        videoUrl: 'All Videos'
      }
    }

    // Specific video insights
    if (!data.metadata?.videos) return null
    const videoData = (data.metadata.videos as any)[selectedVideo]
    if (!videoData) return null

    const videoComments = data.comments.filter((c: any) => {
      const caseField = c.Case
      if (!caseField) return false
      if (selectedVideo === 'sports') return caseField.startsWith('Sports_')
      if (selectedVideo === 'brand') return caseField.startsWith('Brand_')
      if (selectedVideo === 'influencer') return caseField.startsWith('Influencer_')
      return false
    })
    const topComments = videoComments
      .sort((a: any, b: any) => (b.likes || 0) - (a.likes || 0))
      .slice(0, 3)

    return {
      totalComments: videoData.count,
      sentiment: videoData.summary?.sentiment || {},
      contentTypes: videoData.summary?.contentTypes || {},
      topEmotions: Object.entries(videoData.summary?.emotions || {})
        .sort(([, a], [, b]) => (b as number) - (a as number))
        .slice(0, 3)
        .map(([emotion, count]) => ({ emotion, count: count as number })),
      topComments,
      videoUrl: videoData.url || ''
    }
  }, [data, selectedVideo])

  const handleEmojiClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setEmojiAnchorEl(event.currentTarget)
  }

  const handleEmojiClose = () => {
    setEmojiAnchorEl(null)
  }

  const handleEmojiSelect = (emoji: string) => {
    setSearchQuery(prev => prev + emoji)
    handleEmojiClose()
  }

  const emojiPickerOpen = Boolean(emojiAnchorEl)
  const emojiPickerId = emojiPickerOpen ? 'emoji-picker-popover' : undefined

  // Common emojis for TikTok comments
  const commonEmojis = [
    '😂', '🤣', '😭', '💀', '🔥', '❤️', '😍', '🥰',
    '💯', '👏', '🙌', '👍', '👎', '😡', '😤', '🤔',
    '😅', '😆', '🤷', '💪', '🎉', '🤯', '😱', '😳',
    '🤮', '🤢', '💩', '🤡', '👀', '✨', '⚡', '🌟'
  ]

  const handleExport = () => {
    if (!data) return

    const csvContent = [
      ['Author', 'Comment', 'Category', 'Sentiment', 'Likes', 'Replies', 'Themes'],
      ...filteredAndSortedComments.map(c => [
        c.author,
        `"${c.text.replace(/"/g, '""')}"`,
        c.categoryName || c.category || '',
        c.sentiment || '',
        c.likes,
        c.replies,
        c.themes?.map(t => t.name).join('; ') || ''
      ])
    ].map(row => row.join(',')).join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'tiktok-comments.csv'
    a.click()
  }

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <LinearProgress sx={{ width: '50%' }} />
        </Box>
      </Container>
    )
  }

  if (error || !data) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ py: 4 }}>
          <Typography color="error">{error || 'No data available'}</Typography>
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header with Video Selector */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h4">TikTok Comment Analysis</Typography>
              <Typography variant="body2" color="text.secondary">
                {data.total_comments} comments from 3 TikTok videos
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadData}
                size="small"
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                size="small"
              >
                Export CSV
              </Button>
            </Stack>
          </Box>

          {/* Video Thumbnails Selector */}
          <Grid container spacing={2} sx={{ width: 'auto' }}>
            <Grid item>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: selectedVideo === 'all' ? '2px solid #9c27b0' : '1px solid #e0e0e0',
                  '&:hover': { boxShadow: 3 }
                }}
                onClick={() => setSelectedVideo('all')}
              >
                <CardContent sx={{ p: 2 }}>
                  <Stack alignItems="center" spacing={1}>
                    <Box sx={{
                      width: 120,
                      height: 160,
                      bgcolor: '#9c27b0',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography variant="h6" color="white">ALL</Typography>
                    </Box>
                    <Typography variant="caption">All Videos</Typography>
                    <Typography variant="caption" color="text.secondary">324 comments</Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: selectedVideo === 'sports' ? '2px solid #9c27b0' : '1px solid #e0e0e0',
                  '&:hover': { boxShadow: 3 }
                }}
                onClick={() => setSelectedVideo('sports')}
              >
                <CardContent sx={{ p: 2 }}>
                  <Stack alignItems="center" spacing={1}>
                    <Box sx={{
                      width: 120,
                      height: 160,
                      borderRadius: 1,
                      overflow: 'hidden',
                      bgcolor: '#2196f3',
                      position: 'relative'
                    }}>
                      <img
                        src={videoThumbnails.sports}
                        alt="Sky Sports Football"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none'
                        }}
                      />
                      <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        bgcolor: 'rgba(33, 150, 243, 0.9)',
                        zIndex: -1
                      }}>
                        <Typography variant="h2" color="white">⚽</Typography>
                        <Typography variant="caption" color="white">Sky Sports</Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption">@skysportsfootball</Typography>
                    <Typography variant="caption" color="text.secondary">121 comments</Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: selectedVideo === 'brand' ? '2px solid #9c27b0' : '1px solid #e0e0e0',
                  '&:hover': { boxShadow: 3 }
                }}
                onClick={() => setSelectedVideo('brand')}
              >
                <CardContent sx={{ p: 2 }}>
                  <Stack alignItems="center" spacing={1}>
                    <Box sx={{
                      width: 120,
                      height: 160,
                      borderRadius: 1,
                      overflow: 'hidden',
                      bgcolor: '#ff9800',
                      position: 'relative'
                    }}>
                      <img
                        src={videoThumbnails.brand}
                        alt="Hello Sky"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none'
                        }}
                      />
                      <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        bgcolor: 'rgba(255, 152, 0, 0.9)',
                        zIndex: -1
                      }}>
                        <Typography variant="h2" color="white">📺</Typography>
                        <Typography variant="caption" color="white">Hello Sky</Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption">@hellosky</Typography>
                    <Typography variant="caption" color="text.secondary">100 comments</Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: selectedVideo === 'influencer' ? '2px solid #9c27b0' : '1px solid #e0e0e0',
                  '&:hover': { boxShadow: 3 }
                }}
                onClick={() => setSelectedVideo('influencer')}
              >
                <CardContent sx={{ p: 2 }}>
                  <Stack alignItems="center" spacing={1}>
                    <Box sx={{
                      width: 120,
                      height: 160,
                      borderRadius: 1,
                      overflow: 'hidden',
                      bgcolor: '#4caf50',
                      position: 'relative'
                    }}>
                      <img
                        src={videoThumbnails.influencer}
                        alt="Cam Fitzzz"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none'
                        }}
                      />
                      <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        bgcolor: 'rgba(76, 175, 80, 0.9)',
                        zIndex: -1
                      }}>
                        <Typography variant="h2" color="white">🎬</Typography>
                        <Typography variant="caption" color="white">Cam Fitzzz</Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption">@camfitzzz</Typography>
                    <Typography variant="caption" color="text.secondary">103 comments</Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Charts Section with Key Insights and Correlation Charts */}
        {summaryData && (
          <Box sx={{ mb: 4 }}>
            <TikTokAnalytics
              key={selectedVideo}
              data={summaryData}
              comments={(summaryData as any).comments}
              emotionData={(summaryData as any).emotionData}
              selectedVideo={selectedVideo}
            />
          </Box>
        )}

        {/* Deep Dive Section - Now after Key Insights */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <PsychologyIcon color="primary" />
            Deep Dive Analysis
          </Typography>

          {/* 2x2 Grid of charts */}
          <Grid container spacing={3}>
              {/* Category Distribution Chart - Top left */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: 350 }}>
                  <ReactECharts
                    key={`category-${selectedVideo}`}
                    option={categoryChartOption}
                    notMerge={true}
                    lazyUpdate={false}
                    style={{ height: '100%', width: '100%' }}
                  />
                </Paper>
              </Grid>

              {/* Video Insights - Top right */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: 350, bgcolor: 'grey.50' }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InsightsIcon color="primary" />
                    Video Insights
                  </Typography>
                  {videoInsights && (
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Total Comments
                        </Typography>
                        <Typography variant="h4">
                          {videoInsights.totalComments}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Most Liked Comments
                        </Typography>
                        <Stack spacing={1}>
                          {videoInsights.topComments.slice(0, 2).map((comment: any, index: number) => (
                            <Box key={index} sx={{ p: 1, bgcolor: 'white', borderRadius: 1 }}>
                              <Typography variant="caption" sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                lineHeight: 1.4,
                                mb: 0.5
                              }}>
                                "{comment.text}"
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                @{comment.author} • {comment.likes} likes
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    </Stack>
                  )}
                </Paper>
              </Grid>

              {/* Sentiment Pie Chart - Bottom left */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: 350 }}>
                  <ReactECharts
                    key={`sentiment-${selectedVideo}`}
                    option={sentimentChartOption}
                    notMerge={true}
                    lazyUpdate={false}
                    style={{ height: '100%', width: '100%' }}
                  />
                </Paper>
              </Grid>

              {/* Emotion Bar Chart - Bottom right */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: 350 }}>
                  <ReactECharts
                    key={`emotion-${selectedVideo}`}
                    option={emotionChartOption}
                    notMerge={true}
                    lazyUpdate={false}
                    style={{ height: '100%', width: '100%' }}
                  />
                </Paper>
              </Grid>
          </Grid>
        </Paper>

        {/* Filters and Search */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search comments, authors, emojis, or themes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Stack direction="row" spacing={0.5}>
                        {searchQuery && (
                          <IconButton
                            size="small"
                            onClick={() => setSearchQuery('')}
                            sx={{ color: 'text.secondary' }}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={handleEmojiClick}
                          aria-describedby={emojiPickerId}
                          sx={{ color: emojiPickerOpen ? 'primary.main' : 'text.secondary' }}
                        >
                          <EmojiEmotionsIcon />
                        </IconButton>
                      </Stack>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="category-filter-label">Filter by Category</InputLabel>
                <Select
                  labelId="category-filter-label"
                  id="category-filter"
                  value={categoryFilter}
                  label="Filter by Category"
                  onChange={(e: SelectChangeEvent) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  <MenuItem value="substantive" disabled={!availableCategories.has("substantive")}>
                    Substantive {!availableCategories.has("substantive") && "(0)"}
                  </MenuItem>
                  <MenuItem value="brief" disabled={!availableCategories.has("brief")}>
                    Brief {!availableCategories.has("brief") && "(0)"}
                  </MenuItem>
                  <MenuItem value="meme" disabled={!availableCategories.has("meme")}>
                    Meme {!availableCategories.has("meme") && "(0)"}
                  </MenuItem>
                  <MenuItem value="emoji" disabled={!availableCategories.has("emoji")}>
                    Emoji {!availableCategories.has("emoji") && "(0)"}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="sentiment-filter-label">Filter by Sentiment</InputLabel>
                <Select
                  labelId="sentiment-filter-label"
                  id="sentiment-filter"
                  value={sentimentFilter}
                  label="Filter by Sentiment"
                  onChange={(e: SelectChangeEvent) => setSentimentFilter(e.target.value as SentimentFilter)}
                >
                  <MenuItem value="all">All Sentiments</MenuItem>
                  <MenuItem value="positive">Positive</MenuItem>
                  <MenuItem value="neutral">Neutral</MenuItem>
                  <MenuItem value="negative">Negative</MenuItem>
                  <Divider />
                  <MenuItem value="relevant">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Chip label="Positive" size="small" color="success" />
                      <Typography variant="body2">Sky-Relevant</Typography>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="attributed">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Chip label="Positive" size="small" color="success" />
                      <Typography variant="body2">Sky-Attributed</Typography>
                    </Stack>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Results Summary */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Showing {filteredAndSortedComments.length} of {data.total_comments} comments
        </Typography>

        {/* Comments Table */}
        <TableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'author'}
                    direction={orderBy === 'author' ? order : 'asc'}
                    onClick={() => handleRequestSort('author')}
                  >
                    Author
                  </TableSortLabel>
                </TableCell>
                <TableCell width="35%">Comment</TableCell>
                <TableCell align="center">Video</TableCell>
                <TableCell align="center">Category</TableCell>
                <TableCell align="center">Sentiment</TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'likes'}
                    direction={orderBy === 'likes' ? order : 'asc'}
                    onClick={() => handleRequestSort('likes')}
                  >
                    <ThumbUpIcon fontSize="small" />
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'replies'}
                    direction={orderBy === 'replies' ? order : 'asc'}
                    onClick={() => handleRequestSort('replies')}
                  >
                    <CommentIcon fontSize="small" />
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSortedComments
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((comment, index) => {
                  const globalIndex = page * rowsPerPage + index
                  return (
                    <React.Fragment key={globalIndex}>
                      <TableRow hover>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar sx={{ width: 24, height: 24, fontSize: 14 }}>
                              {comment.author[0].toUpperCase()}
                            </Avatar>
                            <Typography variant="body2">{comment.author}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}>
                            {comment.text}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={(() => {
                              const caseField = (comment as any).Case
                              if (!caseField) return 'Unknown'
                              if (caseField.startsWith('Sports_')) return 'Sports'
                              if (caseField.startsWith('Brand_')) return 'Brand'
                              if (caseField.startsWith('Influencer_')) return 'Influencer'
                              return 'Unknown'
                            })()}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: (() => {
                                const caseField = (comment as any).Case
                                if (caseField?.startsWith('Sports_')) return '#1976d2'
                                if (caseField?.startsWith('Brand_')) return '#9c27b0'
                                if (caseField?.startsWith('Influencer_')) return '#ed6c02'
                                return '#757575'
                              })()
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={(() => {
                              const type = (comment as any).contentType || 'unknown'
                              // Capitalize first letter for display
                              return type.charAt(0).toUpperCase() + type.slice(1)
                            })()}
                            size="small"
                            sx={{
                              bgcolor: categoryColors[(comment as any).contentType || 'unknown'] || '#757575',
                              color: 'white',
                              fontWeight: 500
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={comment.sentiment || 'neutral'}>
                            {getSentimentIcon(comment.sentiment)}
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">{comment.likes}</TableCell>
                        <TableCell align="center">{comment.replies}</TableCell>
                      </TableRow>
                    </React.Fragment>
                  )
                })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredAndSortedComments.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10))
              setPage(0)
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      </Box>

      {/* Emoji Picker Popover */}
      <Popover
        id={emojiPickerId}
        open={emojiPickerOpen}
        anchorEl={emojiAnchorEl}
        onClose={handleEmojiClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <Typography variant="subtitle2" gutterBottom>
            Select an emoji
          </Typography>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            gap: 0.5,
            maxHeight: 200,
            overflowY: 'auto',
            overflowX: 'hidden',
            pr: 1
          }}>
            {commonEmojis.map((emoji) => (
              <IconButton
                key={emoji}
                size="small"
                onClick={() => handleEmojiSelect(emoji)}
                sx={{
                  fontSize: '1.5rem',
                  width: 40,
                  height: 40,
                  '&:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
              >
                {emoji}
              </IconButton>
            ))}
          </Box>
        </Box>
      </Popover>
    </Container>
  )
}

export default TiktokAnalysis